import React, { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  ColumnDef,
  ExpandedState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, ChevronRight, Download, Check, X, History, Trash2, Power } from 'lucide-react'
import { GlobalTable, Thead, HeaderLine, Tr, Td, FooterTr, BulkHeaderLine, BulkHeaderTd } from '../styles/table-style'
import { TableTooltipProvider, OverflowCell } from '../OverflowCell'
import LoadMoreButton from '../LoadMoreButton'
import { StandardColumn, RowAction, StandardBulkEditConfig } from './types'
import { ColumnsPreference } from '../../contexts/UserPreferencesContext'
import { resolveColumnOrder, resolveColumnLabel, orderVisibleColumns } from './columnUtils'
import ColumnSettings from './ColumnSettings'
import RowActionsMenu from './RowActionsMenu'
import AuditHistoryModal from './AuditHistoryModal'
import { exportTableToExcel } from './exportToExcel'
import { useTableExportContext } from './TableExportContext'
import { useDelayedLoading } from '../../hooks/useDelayedLoading'
import ApiService from '../../services/ApiService'
import InfiniteSelect from '../InfiniteSelect'
import {
  TableArea,
  TableToolbar,
  ToolbarSlot,
  ToolbarActions,
  ExportButton,
  TableScroll,
  SortableTh,
  ActionsTh,
  ActionsTd,
  GearTh,
  GearTd,
  ThContent,
  MutedSortIcon,
  ActiveSortIcon,
  EmptyMessage,
  ExpandCellContent,
  ExpandToggle,
} from './styles'
import OrdenoLoading from '../OrdenoLoading'

const ToolbarExportWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

const ToolbarExportDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 160px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  padding: 4px 0;
`

const ToolbarExportItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #333333;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f5f5f5;
  }
`

interface StandardDataTableProps<T> {
  columns: StandardColumn<T>[]
  data: T[]
  columnsState: ColumnsPreference
  onColumnsChange?: (next: ColumnsPreference) => void
  onRestoreColumns?: () => void
  totalCount?: number | null
  onLoadMore?: () => void
  onLoadAll?: () => void | Promise<void>
  hasMore?: boolean
  isLoading?: boolean
  countLabel?: string
  renderActions?: (row: T) => ReactNode
  rowActions?: (row: T) => RowAction[]
  actionsHeader?: string
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string
  rowTitle?: (row: T) => string | undefined
  emptyMessage?: string
  toolbarLeft?: ReactNode
  exportFileName?: string
  exportSheetName?: string
  exportRows?: () => T[] | Promise<T[]>
  exportAllRows?: () => T[] | Promise<T[]>
  bulkActions?: RowAction[] | ((selectedIds: Set<string | number>) => RowAction[])
  onBulkDelete?: (selectedIds: Set<string | number>) => void | Promise<void>
  onBulkInactivate?: (selectedIds: Set<string | number>) => void | Promise<void>
  getSubRows?: (row: T) => T[] | undefined
  getRowId?: (row: T) => string
  expandColumnId?: string
  // Expansão controlada (opcional): sem estas props a tabela guarda o estado sozinha.
  expanded?: ExpandedState
  onExpandedChange?: (next: ExpandedState) => void
  selectable?: boolean
  selectedRowIds?: Set<string | number>
  onSelectRow?: (id: string | number, selected: boolean) => void
  onSelectAllRows?: (selected: boolean) => void
  bulkHeaderRow?: ReactNode
  bulkConfig?: StandardBulkEditConfig<T>
  onBulkSave?: (draft: Record<string, any>, selectedIds: (string | number)[]) => void | Promise<void>
  isBulkSaving?: boolean
  selectedCountLabel?: string
  tableName?: string
}

const defaultCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toLocaleDateString('pt-BR')
  if (typeof value === 'object') return ''
  return String(value)
}

const resolveCellTooltipText = <T,>(col: StandardColumn<T>, row: T, rawValue: unknown): string => {
  if (col.exportValue) {
    const value = col.exportValue(row)
    return value === null || value === undefined ? '' : String(value)
  }
  return defaultCellValue(rawValue)
}

function StandardDataTable<T>({
  columns,
  data,
  columnsState,
  onColumnsChange,
  onRestoreColumns,
  totalCount = null,
  onLoadMore,
  onLoadAll,
  hasMore = false,
  isLoading = false,
  countLabel = 'itens',
  renderActions,
  rowActions,
  actionsHeader = 'Ações',
  onRowClick,
  rowClassName,
  rowTitle,
  emptyMessage = 'Nenhum registro encontrado',
  toolbarLeft,
  exportFileName,
  exportSheetName,
  exportRows,
  exportAllRows,
  bulkActions,
  onBulkDelete,
  onBulkInactivate,
  getSubRows,
  getRowId,
  expandColumnId,
  expanded: expandedProp,
  onExpandedChange,
  selectable = true,
  selectedRowIds,
  onSelectRow,
  onSelectAllRows,
  bulkHeaderRow,
  bulkConfig,
  onBulkSave,
  isBulkSaving,
  selectedCountLabel,
  tableName,
}: StandardDataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [expandedInterno, setExpandedInterno] = useState<ExpandedState>({})
  const expanded = expandedProp ?? expandedInterno
  const setExpanded = (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
    const next = typeof updater === 'function' ? updater(expanded) : updater
    if (onExpandedChange) onExpandedChange(next)
    if (expandedProp === undefined) setExpandedInterno(next)
  }
  const [isExporting, setIsExporting] = useState(false)
  const [isToolbarExportOpen, setIsToolbarExportOpen] = useState(false)
  const toolbarExportRef = useRef<HTMLDivElement | null>(null)
  const [internalSelectedRowIds, setInternalSelectedRowIds] = useState<Set<string | number>>(new Set())
  const [internalBulkDraft, setInternalBulkDraft] = useState<Record<string, any>>({})
  const [isInternalBulkSaving, setIsInternalBulkSaving] = useState(false)
  const [auditModalData, setAuditModalData] = useState<{ tableName: string; recordId: number; recordTitle?: string } | null>(null)
  const showLoadingOverlay = useDelayedLoading(isLoading)

  const activeSelectedRowIds = selectedRowIds ?? internalSelectedRowIds
  const selectedCount = activeSelectedRowIds.size

const resolveTableFromPath = (pathname: string): string => {
  const p = (pathname || '').toLowerCase()
  if (p.includes('/resource') || p.includes('/insumo') || p.includes('/aviamento')) return 'Resource'
  if (p.includes('/tax-scenario') || p.includes('/cenario-fiscal')) return 'TaxScenario'
  if (p.includes('/rnc') || p.includes('/occurrence') || p.includes('/ocorrencia')) return 'Occurrence'
  if (p.includes('/params') || p.includes('/parametro')) return 'Structure_Param'
  if (p.includes('/production-fashion') || p.includes('/production_fashion') || p.includes('/production-flow') || p.includes('/production')) return 'Services'
  if (p.includes('/composition') || p.includes('/ficha-tecnica')) return 'Product'
  if (p.includes('/collection') || p.includes('/colecao')) return 'Collection'
  if (p.includes('/product-final') || p.includes('/produto-final')) return 'Product'
  if (p.includes('/product') || p.includes('/produto')) return 'Product'
  if (p.includes('/stock') || p.includes('/estoque')) return 'Stock'
  if (p.includes('/certificate') || p.includes('/certificado')) return 'Certificate'
  if (p.includes('/compan') || p.includes('/empresa') || p.includes('/fornecedor') || p.includes('/cliente')) return 'Company'
  if (p.includes('/fashion') || p.includes('/steel') || p.includes('/order') || p.includes('/pedido')) return 'Orders'
  if (p.includes('/financial') || p.includes('/financeiro')) return 'FinancialEntry'
  if (p.includes('/invoice') || p.includes('/nota-fiscal')) return 'Invoice'
  if (p.includes('/tax') || p.includes('/fiscal')) return 'TaxScenario'
  if (p.includes('/freight') || p.includes('/frete')) return 'Freight'
  if (p.includes('/router') || p.includes('/rota')) return 'Route'
  if (p.includes('/vehicle') || p.includes('/veiculo')) return 'Vehicle'
  if (p.includes('/equipment') || p.includes('/equipamento') || p.includes('/maquina') || p.includes('/machine')) return 'Machine'
  if (p.includes('/pipeline') || p.includes('/funil') || p.includes('/ticket')) return 'Pipeline'
  if (p.includes('/service') || p.includes('/servico') || p.includes('/stage') || p.includes('/etapa')) return 'Stage'
  return 'Stock'
}

  const getRowActions = useCallback(
    (row: T): RowAction[] => {
      const baseActions = rowActions ? rowActions(row) : []
      const recordId = (row as any)?.id || (row as any)?._id || (row as any)?.codeId || (row as any)?.ticketId
      const targetTable = tableName || (typeof window !== 'undefined' ? resolveTableFromPath(window.location.pathname) : 'Stock')
      if (targetTable && recordId && !baseActions.some((a) => a.label?.toLowerCase().includes('auditoria') || a.key === 'audit')) {
        const auditAction: RowAction = {
          key: 'audit',
          label: 'Auditoria',
          icon: <History size={16} color="#2563eb" />,
          onClick: () => {
            const title = (row as any)?.name || (row as any)?.title || (row as any)?.code || (row as any)?.certificateNumber || (row as any)?.companyName
            setAuditModalData({
              tableName: targetTable,
              recordId: Number(recordId),
              recordTitle: title ? String(title) : undefined,
            })
          },
        }
        return [...baseActions, auditAction]
      }
      return baseActions
    },
    [rowActions, tableName],
  )

  const handleRowSelect = useCallback(
    (id: string | number, isSelected: boolean) => {
      if (onSelectRow) {
        onSelectRow(id, isSelected)
      } else {
        setInternalSelectedRowIds((prev) => {
          const next = new Set(prev)
          if (isSelected) next.add(id)
          else next.delete(id)
          return next
        })
      }
    },
    [onSelectRow],
  )

  const handleSelectAll = useCallback(
    (selectAll: boolean) => {
      if (onSelectAllRows) {
        onSelectAllRows(selectAll)
      } else {
        if (selectAll) {
          const allIds = data.map((item: any) => (getRowId ? getRowId(item) : (item.id ?? item)))
          setInternalSelectedRowIds(new Set(allIds))
        } else {
          setInternalSelectedRowIds(new Set())
          setInternalBulkDraft({})
        }
      }
    },
    [onSelectAllRows, data, getRowId],
  )

  const handleClearSelection = useCallback(() => {
    if (bulkConfig?.onClearSelection) {
      bulkConfig.onClearSelection()
    } else {
      if (onSelectAllRows) {
        onSelectAllRows(false)
      }
      setInternalSelectedRowIds(new Set())
      setInternalBulkDraft({})
    }
  }, [bulkConfig, onSelectAllRows])

  const activeBulkDraft = bulkConfig?.bulkDraft ?? internalBulkDraft
  const handleBulkDraftChange = useCallback(
    (field: string, val: any) => {
      if (bulkConfig?.onBulkDraftChange) {
        bulkConfig.onBulkDraftChange(field, val)
      } else {
        setInternalBulkDraft((prev) => ({ ...prev, [field]: val }))
      }
    },
    [bulkConfig],
  )

  const handleSaveBulk = useCallback(async () => {
    if (bulkConfig?.onSave) {
      await bulkConfig.onSave()
    } else if (onBulkSave) {
      setIsInternalBulkSaving(true)
      try {
        await onBulkSave(activeBulkDraft, Array.from(activeSelectedRowIds))
      } finally {
        setIsInternalBulkSaving(false)
      }
    }
  }, [bulkConfig, onBulkSave, activeBulkDraft, activeSelectedRowIds])

  const isSavingBulk = Boolean(bulkConfig?.isSaving || isBulkSaving || isInternalBulkSaving)

  const tableColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((col) => ({
        id: col.id,
        accessorFn: col.accessor ?? ((row: T) => (row as Record<string, unknown>)?.[col.id]),
        header: resolveColumnLabel(col),
        enableSorting: col.sortable !== false,
        cell: (info) => {
          if (col.cell) {
            const rendered = col.cell(info.row.original)
            if (col.disableTooltip) return rendered
            const tooltipText = resolveCellTooltipText(col, info.row.original, info.getValue())
            if (!tooltipText) return rendered
            return <OverflowCell text={tooltipText}>{rendered}</OverflowCell>
          }
          const text = defaultCellValue(info.getValue())
          return <OverflowCell text={text} />
        },
      })),
    [columns],
  )

  const columnVisibility = useMemo(() => {
    const hidden = new Set(columnsState.hidden ?? [])
    return Object.fromEntries(columns.map((col) => [col.id, !hidden.has(col.id)]))
  }, [columns, columnsState])

  const columnOrder = useMemo(() => resolveColumnOrder(columns, columnsState), [columns, columnsState])

  const previousDataMapRef = useRef<Map<string | number, any>>(new Map())
  const touchedTimestampsRef = useRef<Map<string | number, number>>(new Map())
  const isFirstDataRef = useRef(true)

  const resolveItemKey = useCallback(
    (item: any): string | number | null => {
      if (!item || typeof item !== 'object') return null
      if (getRowId) {
        try {
          const customId = getRowId(item)
          if (customId !== undefined && customId !== null) return customId
        } catch (error) {
          void error
        }
      }
      const rawId = item.id ?? item._id ?? item.codeId ?? item.uuid ?? item.key
      return rawId !== undefined && rawId !== null ? rawId : null
    },
    [getRowId],
  )

  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return

    if (isFirstDataRef.current) {
      isFirstDataRef.current = false
      const initialMap = new Map<string | number, any>()
      for (const item of data) {
        const key = resolveItemKey(item)
        if (key !== null) {
          initialMap.set(key, item)
        }
      }
      previousDataMapRef.current = initialMap
      return
    }

    const previousMap = previousDataMapRef.current
    const nextMap = new Map<string | number, any>()
    const newlyAddedKeys: (string | number)[] = []

    for (const item of data) {
      const key = resolveItemKey(item)
      if (key !== null) {
        nextMap.set(key, item)
        if (!previousMap.has(key)) {
          newlyAddedKeys.push(key)
        } else {
          const prevItem = previousMap.get(key) as any
          const currentItem = item as any
          const isChanged =
            currentItem !== prevItem &&
            (currentItem?.updatedAt !== prevItem?.updatedAt ||
              currentItem?.status !== prevItem?.status ||
              currentItem?.name !== prevItem?.name ||
              currentItem?.description !== prevItem?.description ||
              currentItem?.quantity !== prevItem?.quantity ||
              currentItem?.price !== prevItem?.price ||
              JSON.stringify(currentItem) !== JSON.stringify(prevItem))
          if (isChanged) {
            touchedTimestampsRef.current.set(key, Date.now())
          }
        }
      }
    }

    if (newlyAddedKeys.length > 0 && newlyAddedKeys.length <= 2) {
      const now = Date.now()
      for (const key of newlyAddedKeys) {
        touchedTimestampsRef.current.set(key, now)
      }
    }

    previousDataMapRef.current = nextMap
  }, [data, resolveItemKey])

  const sortedData = useMemo(() => {
    if (sorting.length > 0 || !Array.isArray(data) || data.length <= 1) {
      return data
    }

    const getItemScore = (item: any): number => {
      if (!item || typeof item !== 'object') return 0
      const key = resolveItemKey(item)

      const touched = key !== null ? touchedTimestampsRef.current.get(key) : undefined
      if (touched) {
        return touched * 1000
      }

      const updatedVal = item.updatedAt || item.updated_at || item.dataAtualizacao
      if (updatedVal) {
        const parsed = new Date(updatedVal).getTime()
        if (!isNaN(parsed) && parsed > 0) return parsed
      }

      const createdVal = item.createdAt || item.created_at || item.dataCriacao
      if (createdVal) {
        const parsed = new Date(createdVal).getTime()
        if (!isNaN(parsed) && parsed > 0) return parsed
      }

      const numericId = Number(item.id ?? item._id ?? item.codeId)
      if (!isNaN(numericId) && numericId > 0) {
        return numericId
      }

      return 0
    }

    const hasAnyScore = data.some((item) => getItemScore(item) > 0)
    if (!hasAnyScore) {
      return data
    }

    return [...data].sort((a, b) => {
      const scoreA = getItemScore(a)
      const scoreB = getItemScore(b)
      if (scoreA !== scoreB) {
        return scoreB - scoreA
      }
      return 0
    })
  }, [data, sorting, resolveItemKey])

  const table = useReactTable({
    data: sortedData,
    columns: tableColumns,
    state: { sorting, columnVisibility, columnOrder, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getSubRows,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  })

  const rows = table.getRowModel().rows
  const hasActionsColumn = !!rowActions || !!renderActions || !!tableName
  const exportContext = useTableExportContext()
  const exportRegistrationId = useId()
  const showColumnSettings = !!onColumnsChange && !!onRestoreColumns
  const showExport = !!exportFileName
  const showToolbarExport = showExport && !exportContext?.hasPageHeader
  const visibleColumnCount =
    table.getVisibleLeafColumns().length + (hasActionsColumn ? 1 : 0) + (showColumnSettings ? 1 : 0) + (selectable ? 1 : 0)

  const allSelected =
    data.length > 0 &&
    activeSelectedRowIds.size > 0 &&
    data.every((item: any) => activeSelectedRowIds.has(getRowId ? getRowId(item) : item.id))

  useEffect(() => {
    if (!isToolbarExportOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (toolbarExportRef.current && !toolbarExportRef.current.contains(e.target as Node)) {
        setIsToolbarExportOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isToolbarExportOpen])

  const handleExport = useCallback(async (mode: 'table' | 'all' = 'table') => {
    if (!exportFileName || isExporting) return
    setIsExporting(true)
    try {
      let rowsToExport: any[]
      if (mode === 'all') {
        if (exportAllRows) {
          rowsToExport = await exportAllRows()
        } else if (exportRows) {
          rowsToExport = await exportRows()
        } else {
          rowsToExport = data
        }
      } else {
        rowsToExport = exportRows
          ? await exportRows()
          : table.getRowModel().rows.filter((row) => row.depth === 0).map((row) => row.original)
      }
      const effectiveColumnsState = mode === 'all'
        ? { ...columnsState, hidden: [] }
        : columnsState
      await exportTableToExcel({
        fileName: exportFileName,
        sheetName: exportSheetName,
        columns,
        columnsState: effectiveColumnsState,
        rows: rowsToExport,
      })
    } finally {
      setIsExporting(false)
    }
  }, [exportFileName, exportSheetName, exportRows, exportAllRows, isExporting, table, columns, columnsState, data])

  const registerTableExport = exportContext?.registerTableExport
  const unregisterTableExport = exportContext?.unregisterTableExport

  useEffect(() => {
    if (!registerTableExport || !unregisterTableExport || !exportFileName) return
    registerTableExport({
      id: exportRegistrationId,
      onExport: () => handleExport('table'),
      onExportTable: () => handleExport('table'),
      onExportAll: () => handleExport('all'),
      isExporting,
      disabled: data.length === 0,
    })
    return () => unregisterTableExport(exportRegistrationId)
  }, [registerTableExport, unregisterTableExport, exportFileName, exportRegistrationId, handleExport, isExporting, data.length])

  const registerTable = exportContext?.registerTable
  const unregisterTable = exportContext?.unregisterTable

  useEffect(() => {
    if (!registerTable || !unregisterTable) return
    registerTable(exportRegistrationId)
    return () => unregisterTable(exportRegistrationId)
  }, [registerTable, unregisterTable, exportRegistrationId])

  const effectiveBulkActions: RowAction[] = useMemo(() => {
    if (selectedCount === 0) return []
    let customActions: RowAction[] = []
    if (typeof bulkActions === 'function') {
      customActions = bulkActions(activeSelectedRowIds)
    } else if (Array.isArray(bulkActions)) {
      customActions = bulkActions
    }
    const result = [...customActions]
    if (onBulkInactivate && !result.some((a) => a.label.toLowerCase().includes('inativar'))) {
      result.push({
        label: 'Inativar em massa',
        icon: <Power size={16} />,
        onClick: () => onBulkInactivate(activeSelectedRowIds),
      })
    }
    if (onBulkDelete && !result.some((a) => a.label.toLowerCase().includes('excluir'))) {
      result.push({
        label: 'Excluir em massa',
        icon: <Trash2 size={16} />,
        danger: true,
        onClick: () => onBulkDelete(activeSelectedRowIds),
      })
    }
    return result
  }, [selectedCount, bulkActions, activeSelectedRowIds, onBulkInactivate, onBulkDelete])

  const defaultBulkToolbar = useMemo(() => {
    if (selectedCount === 0) return null
    const label = bulkConfig?.selectedCountLabel || selectedCountLabel || countLabel || 'itens'
    const canSave = Boolean(bulkConfig?.onSave || onBulkSave)
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span
          style={{
            backgroundColor: '#0090d3',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 600,
            padding: '4px 12px',
            borderRadius: '16px',
          }}
        >
          {selectedCount} {label} selecionados
        </span>

        {canSave && (
          <button
            type="button"
            onClick={handleSaveBulk}
            disabled={isSavingBulk}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '6px',
              backgroundColor: '#10b981',
              color: '#fff',
              border: 'none',
              fontSize: '12px',
              fontWeight: 600,
              cursor: isSavingBulk ? 'not-allowed' : 'pointer',
              opacity: isSavingBulk ? 0.7 : 1,
            }}
          >
            <Check size={14} />
            {`Salvar ${selectedCount} ${label} Editado(s)`}
          </button>
        )}

        <button
          type="button"
          onClick={handleClearSelection}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 12px',
            borderRadius: '6px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '1px solid #d1d5db',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <X size={14} />
          Limpar Seleção
        </button>
      </div>
    )
  }, [
    selectedCount,
    bulkConfig,
    selectedCountLabel,
    countLabel,
    onBulkSave,
    handleSaveBulk,
    isSavingBulk,
    handleClearSelection,
  ])

  const visibleColumnsList = useMemo<StandardColumn<T>[]>(
    () => orderVisibleColumns<T>(columns, columnsState),
    [columns, columnsState],
  )

const isUniqueOrReadOnlyColumn = (colId: string) => {
  const normalizedId = colId.toLowerCase()
  return [
    'id',
    '_id',
    'code',
    'codeid',
    'codigo',
    'cod',
    'cnpj',
    'cpf',
    'doc',
    'documento',
    'plate',
    'placa',
    'invoice',
    'notafiscal',
    'nfe',
    'certificateurl',
    'certificado',
    'quantities',
    'prices',
    'costs',
    'precos',
    'custos',
    'items',
    'itens',
    'cost',
    'custo',
    'price',
    'preco',
    'preço',
    'attachments',
    'anexos',
    'script',
    'actions',
    'acoes',
    'selected',
    'selection',
  ].includes(normalizedId)
}

const getCompanyLabel = (item: any): string => {
  if (!item) return ''
  const baseName = (item.companyName || item.contactName || item.name || '').toString().trim()
  if (!baseName) return ''
  if (!item.isMain) return baseName
  const tipo = item.tipoEstabelecimento || (item.cnpj ? (item.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '')
  return tipo ? `${baseName} - ${tipo}` : baseName
}

const renderDefaultBulkInputForColumn = (
  colId: string,
  val: any,
  idVal: any,
  activeBulkDraft: Record<string, any>,
  onChange: (colId: string, value: any, idValue?: any) => void,
) => {
  const normalizedId = colId.toLowerCase()
  const isSupplierField = ['supplier', 'suppliers', 'fornecedor', 'fornecedores'].includes(normalizedId)
  const selectedOption = val && !isSupplierField ? { id: idVal || val, name: String(val) } : null

  if (isUniqueOrReadOnlyColumn(colId)) {
    return null
  }

  if (normalizedId === 'isactivated') {
    return (
      <select
        value={val}
        onChange={(e) => onChange(colId, e.target.value)}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: '11px',
          borderRadius: '4px',
          border: '1px solid #0090d3',
          backgroundColor: '#fff',
          cursor: 'pointer',
        }}
      >
        <option value="">(Sem alteração)</option>
        <option value="Ativo">Ativo</option>
        <option value="Inativo">Inativo</option>
      </select>
    )
  }

  const lookupMap: Record<string, {
    fetch: (search?: string, limit?: number, offset?: number) => Promise<any>
    create?: (name: string) => Promise<any>
    delete?: (id: any) => Promise<any>
  }> = {
    category: {
      fetch: async () => {
        const isService = typeof window !== 'undefined' && window.location.pathname.includes('service')
        return isService ? ApiService.services.getCategories() : ApiService.resources.getCategories()
      },
      create: async (name) => {
        const isService = typeof window !== 'undefined' && window.location.pathname.includes('service')
        return isService ? ApiService.services.createCategory({ name }) : ApiService.resources.createCategory({ name })
      },
      delete: async (id) => {
        const isService = typeof window !== 'undefined' && window.location.pathname.includes('service')
        return isService ? ApiService.services.deleteCategory(id) : ApiService.resources.deleteCategory(id)
      },
    },
    categoria: {
      fetch: async () => ApiService.resources.getCategories(),
      create: async (name) => ApiService.resources.createCategory({ name }),
      delete: async (id) => ApiService.resources.deleteCategory(id),
    },
    type: {
      fetch: async (searchVal?: string) => {
        const isService = typeof window !== 'undefined' && window.location.pathname.includes('service')
        const isCompany = typeof window !== 'undefined' && window.location.pathname.includes('compan')
        if (isService) {
          const catId = activeBulkDraft.categoryId || activeBulkDraft.category
          if (!catId) return []
          return ApiService.services.getTypes({ categoryId: catId, query: searchVal })
        }
        if (isCompany) {
          const resp = await ApiService.companies.getTypes()
          const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : ['Cliente', 'Fornecedor', 'Transportadora', 'Empresa Própria']
          return list.map((t: any) => typeof t === 'string' ? { id: t, name: t } : { id: t.id || t.typeName || t.name, name: t.typeName || t.name })
        }
        const catId = activeBulkDraft.categoryId || activeBulkDraft.category
        if (!catId) return []
        return ApiService.resources.getTypes({ categoryId: catId, query: searchVal })
      },
      create: async (name) => {
        const isService = typeof window !== 'undefined' && window.location.pathname.includes('service')
        const isCompany = typeof window !== 'undefined' && window.location.pathname.includes('compan')
        if (isCompany) {
          return { id: name, name }
        }
        const catId = activeBulkDraft.categoryId || activeBulkDraft.category
        if (!catId) {
          alert('Para criar um novo tipo, selecione primeiro uma Categoria no lote.')
          return { id: '', name: '' }
        }
        return isService
          ? ApiService.services.createType({ name, categoryId: catId })
          : ApiService.resources.createType({ name, categoryId: catId })
      },
      delete: async (id) => {
        const isService = typeof window !== 'undefined' && window.location.pathname.includes('service')
        return isService ? ApiService.services.deleteType(id) : ApiService.resources.deleteType(id)
      },
    },
    tipo: {
      fetch: async (searchVal?: string) => {
        const isCompany = typeof window !== 'undefined' && window.location.pathname.includes('compan')
        if (isCompany) {
          const resp = await ApiService.companies.getTypes()
          const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : ['Cliente', 'Fornecedor', 'Transportadora', 'Empresa Própria']
          return list.map((t: any) => typeof t === 'string' ? { id: t, name: t } : { id: t.id || t.typeName || t.name, name: t.typeName || t.name })
        }
        const catId = activeBulkDraft.categoryId || activeBulkDraft.category || activeBulkDraft.categoria
        if (!catId) return []
        return ApiService.resources.getTypes({ categoryId: catId, query: searchVal })
      },
      create: async (name) => {
        const isCompany = typeof window !== 'undefined' && window.location.pathname.includes('compan')
        if (isCompany) {
          return { id: name, name }
        }
        const catId = activeBulkDraft.categoryId || activeBulkDraft.category || activeBulkDraft.categoria
        if (!catId) {
          alert('Para criar um novo tipo, selecione primeiro uma Categoria no lote.')
          return { id: '', name: '' }
        }
        return ApiService.resources.createType({ name, categoryId: catId })
      },
      delete: async (id) => ApiService.resources.deleteType(id),
    },
    companytypes: {
      fetch: async () => {
        const resp = await ApiService.companies.getTypes()
        const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : ['Cliente', 'Fornecedor', 'Transportadora', 'Empresa Própria']
        return list.map((t: any) => typeof t === 'string' ? { id: t, name: t } : { id: t.id || t.typeName || t.name, name: t.typeName || t.name })
      },
    },
    companytype: {
      fetch: async () => {
        const resp = await ApiService.companies.getTypes()
        const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : ['Cliente', 'Fornecedor', 'Transportadora', 'Empresa Própria']
        return list.map((t: any) => typeof t === 'string' ? { id: t, name: t } : { id: t.id || t.typeName || t.name, name: t.typeName || t.name })
      },
    },
    color: {
      fetch: async () => ApiService.resources.getColors(),
      create: async (name) => ApiService.resources.createColor({ name }),
      delete: async (id) => ApiService.resources.deleteColor(id),
    },
    cor: {
      fetch: async () => ApiService.resources.getColors(),
      create: async (name) => ApiService.resources.createColor({ name }),
      delete: async (id) => ApiService.resources.deleteColor(id),
    },
    material: {
      fetch: async () => ApiService.stockLookup.getMaterials({ type: 'STOCK' }),
      create: async (name) => ApiService.stockLookup.createMaterial({ name, type: 'STOCK' }),
      delete: async (id) => ApiService.stockLookup.deleteMaterial(id),
    },
    materiaprima: {
      fetch: async () => ApiService.stockLookup.getMaterials({ type: 'STOCK' }),
      create: async (name) => ApiService.stockLookup.createMaterial({ name, type: 'STOCK' }),
      delete: async (id) => ApiService.stockLookup.deleteMaterial(id),
    },
    format: {
      fetch: async () => ApiService.stockLookup.getFormats(),
      create: async (name) => ApiService.stockLookup.createFormat({ name }),
      delete: async (id) => ApiService.stockLookup.deleteFormat(id),
    },
    formato: {
      fetch: async () => ApiService.stockLookup.getFormats(),
      create: async (name) => ApiService.stockLookup.createFormat({ name }),
      delete: async (id) => ApiService.stockLookup.deleteFormat(id),
    },
    finishing: {
      fetch: async () => ApiService.stockLookup.getFinishings(),
      create: async (name) => ApiService.stockLookup.createFinishing({ name }),
      delete: async (id) => ApiService.stockLookup.deleteFinishing(id),
    },
    acabamento: {
      fetch: async () => ApiService.stockLookup.getFinishings(),
      create: async (name) => ApiService.stockLookup.createFinishing({ name }),
      delete: async (id) => ApiService.stockLookup.deleteFinishing(id),
    },
    fabrication: {
      fetch: async () => ApiService.stockLookup.getFabrications(),
      create: async (name) => ApiService.stockLookup.createFabrication({ name }),
      delete: async (id) => ApiService.stockLookup.deleteFabrication(id),
    },
    fabricacao: {
      fetch: async () => ApiService.stockLookup.getFabrications(),
      create: async (name) => ApiService.stockLookup.createFabrication({ name }),
      delete: async (id) => ApiService.stockLookup.deleteFabrication(id),
    },
    heattreatment: {
      fetch: async () => ApiService.stockLookup.getHeatTreatments(),
      create: async (name) => ApiService.stockLookup.createHeatTreatment({ name }),
      delete: async (id) => ApiService.stockLookup.deleteHeatTreatment(id),
    },
    origincountry: {
      fetch: async () => ApiService.stockLookup.getOriginCountries(),
      create: async (name) => ApiService.stockLookup.createOriginCountry({ name }),
      delete: async (id) => ApiService.stockLookup.deleteOriginCountry(id),
    },
    shelf: {
      fetch: async () => ApiService.stockLookup.getShelfs(),
      create: async (name) => ApiService.stockLookup.createShelf({ name }),
      delete: async (id) => ApiService.stockLookup.deleteShelf(id),
    },
    prateleira: {
      fetch: async () => ApiService.stockLookup.getShelfs(),
      create: async (name) => ApiService.stockLookup.createShelf({ name }),
      delete: async (id) => ApiService.stockLookup.deleteShelf(id),
    },
    stocklocation: {
      fetch: async () => ApiService.stockLookup.getStockLocations(),
      create: async (name) => ApiService.stockLookup.createStockLocation({ name }),
      delete: async (id) => ApiService.stockLookup.deleteStockLocation(id),
    },
    localizacao: {
      fetch: async () => ApiService.stockLookup.getStockLocations(),
      create: async (name) => ApiService.stockLookup.createStockLocation({ name }),
      delete: async (id) => ApiService.stockLookup.deleteStockLocation(id),
    },
    stockstreet: {
      fetch: async () => ApiService.stockLookup.getStockStreets(),
      create: async (name) => ApiService.stockLookup.createStockStreet({ name }),
      delete: async (id) => ApiService.stockLookup.deleteStockStreet(id),
    },
    rua: {
      fetch: async () => ApiService.stockLookup.getStockStreets(),
      create: async (name) => ApiService.stockLookup.createStockStreet({ name }),
      delete: async (id) => ApiService.stockLookup.deleteStockStreet(id),
    },
    ncm: {
      fetch: async (searchVal?: string, limit?: number, offset?: number) => {
        const response = await ApiService.stockLookup.searchNcmCatalogo(searchVal, limit, offset)
        const items = Array.isArray(response?.data) ? response.data : []
        return items.map((item: any) => ({
          id: item.id,
          name: `${item.ncm}${item.descricao ? ` · ${item.descricao}` : ''}${item.jaCadastrado ? ' · em uso' : ''}`,
          ncm: item.ncm,
          descricao: item.descricao,
          jaCadastrado: item.jaCadastrado,
        }))
      },
      // sem create/delete: NCM só existe se estiver no catálogo oficial (S5); allowCreate/allowDelete
      // ficam false automaticamente (linhas abaixo checam Boolean(lookupConfig.create/delete))
    },
    stockstatus: {
      fetch: async () => ApiService.stockLookup.getStockStatuses(),
      create: async (name) => ApiService.stockLookup.createStockStatus({ name }),
      delete: async (id) => ApiService.status.deleteStatus(id),
    },
    family: {
      fetch: async () => ApiService.stockLookup.getFamilies(),
      create: async (name) => ApiService.stockLookup.createFamily({ name }),
      delete: async (id) => ApiService.stockLookup.deleteFamily(id),
    },
    heatnumber: {
      fetch: async () => ApiService.stockLookup.getHeatNumbers(),
      create: async (name) => ApiService.stockLookup.createHeatNumber({ name }),
      delete: async (id) => ApiService.stockLookup.deleteHeatNumber(id),
    },
    area: {
      fetch: async () => ApiService.services.getAreas(),
      create: async (name) => ApiService.services.createArea({ name }),
      delete: async (id) => ApiService.services.deleteArea(id),
    },
    sector: {
      fetch: async () => {
        const areaId = activeBulkDraft.areaId || activeBulkDraft.area
        return ApiService.services.getSectors(areaId ? { areaId } : undefined)
      },
      create: async (name) => {
        const areaId = activeBulkDraft.areaId || activeBulkDraft.area
        return ApiService.services.createSector({ name, areaId })
      },
      delete: async (id) => ApiService.services.deleteSector(id),
    },
    setor: {
      fetch: async () => {
        const areaId = activeBulkDraft.areaId || activeBulkDraft.area
        return ApiService.services.getSectors(areaId ? { areaId } : undefined)
      },
      create: async (name) => {
        const areaId = activeBulkDraft.areaId || activeBulkDraft.area
        return ApiService.services.createSector({ name, areaId })
      },
      delete: async (id) => ApiService.services.deleteSector(id),
    },
    machine: {
      fetch: async () => ApiService.services.getMachines(),
      create: async (name) => ApiService.services.createMachine({ name }),
      delete: async (id) => ApiService.services.deleteMachine(id),
    },
    maquina: {
      fetch: async () => ApiService.services.getMachines(),
      create: async (name) => ApiService.services.createMachine({ name }),
      delete: async (id) => ApiService.services.deleteMachine(id),
    },
    supplier: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '', companyType: 'Fornecedor' }),
    },
    suppliers: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '', companyType: 'Fornecedor' }),
    },
    fornecedor: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '', companyType: 'Fornecedor' }),
    },
    fornecedores: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '', companyType: 'Fornecedor' }),
    },
    company: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '' }),
    },
    empresa: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '' }),
    },
    client: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '' }),
    },
    cliente: {
      fetch: async (searchVal) => ApiService.companies.findAll({ query: searchVal || '' }),
    },
    paymentcondition: {
      fetch: async () => ApiService.steelOrders.getPaymentConditions(),
      create: async (name) => ApiService.steelOrders.createPaymentCondition({ name }),
    },
    representative: {
      fetch: async () => ApiService.steelOrders.getRepresentatives(),
      create: async (name) => ApiService.steelOrders.createRepresentative({ name }),
      delete: async (id) => ApiService.steelOrders.deleteRepresentative(id),
    },
    ordersituation: {
      fetch: async () => ApiService.steelOrders.getOrderSituations(),
      create: async (name) => ApiService.steelOrders.createOrderSituation({ name }),
      delete: async (id) => ApiService.steelOrders.deleteOrderSituation(id),
    },
    generalclass: {
      fetch: async () => ApiService.steelOrders.getGeneralClasses(),
      create: async (name) => ApiService.steelOrders.createGeneralClass({ name }),
      delete: async (id) => ApiService.steelOrders.deleteGeneralClass(id),
    },
    ordertype: {
      fetch: async () => ApiService.steelOrders.getOrderTypes(),
      create: async (name) => ApiService.steelOrders.createOrderType({ name }),
      delete: async (id) => ApiService.steelOrders.deleteOrderType(id),
    },
    trackingnumber: {
      fetch: async (searchVal) => {
        const response = await ApiService.get('/backend/trackingNumber/list')
        const items = Array.isArray(response?.data) ? response.data : []
        return items.map((item: any) => ({
          id: item.id || item.number || item.name,
          name: item.name || item.number || String(item.id),
        }))
      },
      create: async (name: string) => {
        const response = await ApiService.post('/backend/trackingNumber', { number: name.trim() })
        return { id: response.data.id, name: response.data.number || name.trim() }
      },
      delete: async (id: any) => {
        await ApiService.delete(`/backend/trackingNumber/${id}`)
      },
    },
    numeroderastreamento: {
      fetch: async (searchVal) => {
        const response = await ApiService.get('/backend/trackingNumber/list')
        const items = Array.isArray(response?.data) ? response.data : []
        return items.map((item: any) => ({
          id: item.id || item.number || item.name,
          name: item.name || item.number || String(item.id),
        }))
      },
      create: async (name: string) => {
        const response = await ApiService.post('/backend/trackingNumber', { number: name.trim() })
        return { id: response.data.id, name: response.data.number || name.trim() }
      },
      delete: async (id: any) => {
        await ApiService.delete(`/backend/trackingNumber/${id}`)
      },
    },
    purchaseorder: {
      fetch: async (searchVal) => {
        try {
          const response = await ApiService.get('/backend/purchaseOrder')
          const items = Array.isArray(response?.data) ? response.data : []
          return items
            .map((item: any, idx: number) => {
              if (typeof item === 'string') return { id: item, name: item }
              const name = item.name || item.number || item.purchaseOrder || ''
              return { id: item.id || name || `po-${idx}`, name }
            })
            .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')
        } catch {
          return []
        }
      },
      create: async (name: string) => {
        try {
          const response = await ApiService.post('/backend/purchaseOrder', { name: name.trim() })
          return { id: response.data?.id || name.trim(), name: response.data?.name || name.trim() }
        } catch {
          return { id: name.trim(), name: name.trim() }
        }
      },
      delete: async (id: any) => {
        try {
          await ApiService.delete(`/backend/purchaseOrder/${id}`)
        } catch (_err) {
          void _err
        }
      },
    },
    ordemdecompra: {
      fetch: async (searchVal) => {
        try {
          const response = await ApiService.get('/backend/purchaseOrder')
          const items = Array.isArray(response?.data) ? response.data : []
          return items
            .map((item: any, idx: number) => {
              if (typeof item === 'string') return { id: item, name: item }
              const name = item.name || item.number || item.purchaseOrder || ''
              return { id: item.id || name || `po-${idx}`, name }
            })
            .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')
        } catch (_err) {
          void _err
          return []
        }
      },
      create: async (name: string) => {
        try {
          const response = await ApiService.post('/backend/purchaseOrder', { name: name.trim() })
          return { id: response.data?.id || name.trim(), name: response.data?.name || name.trim() }
        } catch (_err) {
          void _err
          return { id: name.trim(), name: name.trim() }
        }
      },
      delete: async (id: any) => {
        try {
          await ApiService.delete(`/backend/purchaseOrder/${id}`)
        } catch (_err) {
          void _err
        }
      },
    },
  }

  const lookupConfig = lookupMap[normalizedId]

  if (lookupConfig) {
    const rawSupplierList: Array<{ id: any; name: string }> = isSupplierField
      ? Array.isArray(val)
        ? val
        : typeof val === 'string' && val.trim()
          ? val.split(',').map((s) => ({ id: s.trim(), name: s.trim() })).filter((s) => s.name)
          : []
      : []

    const isTypeField = ['type', 'tipo'].includes(normalizedId)
    const isCompanyPath = typeof window !== 'undefined' && window.location.pathname.includes('compan')
    const hasCategory = Boolean(activeBulkDraft.categoryId || activeBulkDraft.category || activeBulkDraft.categoria)
    const isTypeDisabled = isTypeField && !isCompanyPath && !hasCategory

    return (
      <div style={{ minWidth: '140px', maxWidth: '240px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <InfiniteSelect
          key={`${colId}-${activeBulkDraft.categoryId || activeBulkDraft.category || activeBulkDraft.categoria || 'none'}`}
          mode="crud"
          value={selectedOption}
          categoryId={activeBulkDraft.categoryId || activeBulkDraft.category || activeBulkDraft.categoria}
          placeholder={
            isTypeDisabled
              ? 'Selecione uma categoria primeiro'
              : isSupplierField
                ? rawSupplierList.length > 0
                  ? '+ Adicionar...'
                  : 'Selecionar...'
                : 'Alterar...'
          }
          searchPlaceholder="Buscar ou criar..."
          disabled={isTypeDisabled}
          errorOptionsMessage={normalizedId === 'ncm' ? 'Código não existe na tabela oficial de NCM' : undefined}
          onSelect={(option) => {
            if (normalizedId === 'ncm') {
              if (!option) {
                onChange(colId, '', '')
                return
              }
              onChange(colId, option.ncm ?? option.name, option.id)
              return
            }
            if (!option) {
              if (!isSupplierField) {
                onChange(colId, '', '')
                if (['category', 'categoria'].includes(normalizedId)) {
                  onChange('type', '', '')
                  onChange('tipo', '', '')
                }
              }
            } else {
              const name = getCompanyLabel(option) || option.name || option.type || option.companyName || option.plate || String(option.id)
              if (isSupplierField) {
                const nextList = rawSupplierList.some((s) => s.name === name || s.id === option.id)
                  ? rawSupplierList
                  : [...rawSupplierList, { id: option.id, name }]
                onChange(colId, nextList.map((s) => s.name).join(', '), nextList)
              } else {
                onChange(colId, name, option.id)
                if (['category', 'categoria'].includes(normalizedId)) {
                  onChange('type', '', '')
                  onChange('tipo', '', '')
                }
              }
            }
          }}
          showPantoneSwatch={false}
          fetchFunction={async (_limit, _offset, searchVal) => {
            if (normalizedId === 'ncm') {
              // o catálogo já devolve { id, name, ncm, descricao, jaCadastrado } prontos e filtrados
              // no servidor (prefixo sem pontos ou descrição) — não remapear/refiltrar aqui.
              return lookupConfig.fetch(searchVal, _limit, _offset)
            }
            const response = await lookupConfig.fetch(searchVal)
            const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []
            const mapped = items
              .map((item: any, idx: number) => {
                if (typeof item === 'string') return { id: item, name: item }
                const name = getCompanyLabel(item) || item.name || item.type || item.ncm || item.companyName || item.plate || ''
                return {
                  id: item.id || name || `temp-${idx}`,
                  name: name,
                }
              })
              .filter((item: any) => item.name && item.name.toLowerCase() !== 'null' && item.name.toLowerCase() !== 'undefined')

            return searchVal
              ? mapped.filter((item: any) => item.name?.toLowerCase().includes(searchVal.toLowerCase()))
              : mapped
          }}
          onItemCreate={
            lookupConfig.create
              ? async (name: string) => {
                  const trimmedName = name.trim()
                  const response = await lookupConfig.create!(trimmedName)
                  const resData = response?.data || {}
                  return { id: resData.id || trimmedName, name: resData.name || resData.type || trimmedName }
                }
              : undefined
          }
          onItemDelete={
            lookupConfig.delete
              ? async (id: any) => {
                  await lookupConfig.delete!(id)
                }
              : undefined
          }
          allowCreate={isTypeDisabled ? false : Boolean(lookupConfig.create)}
          allowDelete={isTypeDisabled ? false : Boolean(lookupConfig.delete)}
          showCreateOnlyIfNotExists={true}
          minCreateLength={2}
        />
        {isSupplierField && rawSupplierList.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxHeight: '60px', overflowY: 'auto' }}>
            {rawSupplierList.map((sup, idx) => (
              <span
                key={idx}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  backgroundColor: '#e0f2fe',
                  color: '#0369a1',
                  fontSize: '10px',
                  fontWeight: 500,
                  padding: '1px 5px',
                  borderRadius: '10px',
                  border: '1px solid #bae6fd',
                }}
              >
                {sup.name}
                <X
                  size={10}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const nextList = rawSupplierList.filter((_, i) => i !== idx)
                    onChange(colId, nextList.map((s) => s.name).join(', '), nextList)
                  }}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    )
  }

  const isDateField =
    normalizedId.includes('date') ||
    normalizedId.includes('data') ||
    normalizedId.endsWith('at') ||
    [
      'receiveddate',
      'createdat',
      'updatedat',
      'deliverydate',
      'entrydate',
      'duedate',
      'emissiondate',
      'orderdate',
      'birthdate',
      'validationdate',
      'expirationdate',
    ].includes(normalizedId)

  if (isDateField) {
    const formatDateValueForInput = (raw: any): string => {
      if (!raw) return ''
      try {
        const d = new Date(raw)
        if (!isNaN(d.getTime())) {
          return d.toISOString().split('T')[0]
        }
      } catch (_err) {
        void _err
      }
      if (typeof raw === 'string' && raw.match(/^\d{4}-\d{2}-\d{2}/)) {
        return raw.slice(0, 10)
      }
      return String(raw)
    }

    return (
      <input
        type="date"
        value={formatDateValueForInput(val)}
        onChange={(e) => onChange(colId, e.target.value)}
        style={{
          width: '100%',
          padding: '4px 6px',
          fontSize: '11px',
          borderRadius: '4px',
          border: '1px solid #0090d3',
          backgroundColor: '#fff',
          boxSizing: 'border-box',
          cursor: 'pointer',
        }}
      />
    )
  }

  return (
    <input
      type="text"
      placeholder="Alterar..."
      value={val}
      onChange={(e) => onChange(colId, e.target.value)}
      style={{
        width: '100%',
        padding: '4px 6px',
        fontSize: '11px',
        borderRadius: '4px',
        border: '1px solid #0090d3',
        backgroundColor: '#fff',
        boxSizing: 'border-box',
      }}
    />
  )
}

  const effectiveBulkHeaderRow = useMemo(() => {
    if (bulkHeaderRow) return bulkHeaderRow
    if (selectedCount === 0) return null

    return (
      <BulkHeaderLine>
        {selectable && (
          <BulkHeaderTd style={{ width: '40px', padding: '4px', textAlign: 'center' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#0090d3' }}>Lote</span>
          </BulkHeaderTd>
        )}
        {visibleColumnsList.map((col: StandardColumn<T>) => {
          const val = activeBulkDraft[col.id] ?? ''
          const idVal = activeBulkDraft[`${col.id}Id`]
          let inputNode: ReactNode = null
          if (col.disableBulkEdit) {
            inputNode = null
          } else if (col.bulkInput) {
            inputNode = col.bulkInput({
              value: val,
              onChange: (nextVal: any) => handleBulkDraftChange(col.id, nextVal),
              selectedCount,
            })
          } else if (bulkConfig?.renderBulkInput) {
            inputNode = bulkConfig.renderBulkInput(col.id, col)
          } else {
            inputNode = renderDefaultBulkInputForColumn(col.id, val, idVal, activeBulkDraft, (field, value, idValue) => {
              handleBulkDraftChange(field, value)
              if (idValue !== undefined) {
                handleBulkDraftChange(`${field}Id`, idValue)
              }
            })
          }
          return (
            <BulkHeaderTd key={col.id}>
              {inputNode}
            </BulkHeaderTd>
          )
        })}
        {hasActionsColumn && (
          <BulkHeaderTd style={{ padding: '4px 6px', textAlign: 'center' }}>
            {selectedCount > 0 && effectiveBulkActions.length > 0 && (
              <RowActionsMenu actions={effectiveBulkActions} />
            )}
          </BulkHeaderTd>
        )}
        {showColumnSettings && <BulkHeaderTd style={{ padding: '4px' }} />}
      </BulkHeaderLine>
    )
  }, [
    bulkHeaderRow,
    selectedCount,
    selectable,
    visibleColumnsList,
    activeBulkDraft,
    handleBulkDraftChange,
    bulkConfig,
    hasActionsColumn,
    showColumnSettings,
    effectiveBulkActions,
  ])

  const effectiveToolbarLeft = toolbarLeft || defaultBulkToolbar
  const showToolbar = Boolean(effectiveToolbarLeft || showToolbarExport)

  return (
    <TableTooltipProvider>
      <TableArea>
        {showToolbar && (
          <TableToolbar>
            <ToolbarSlot>{effectiveToolbarLeft}</ToolbarSlot>
            <ToolbarActions>
              {showToolbarExport && (
                <ToolbarExportWrapper ref={toolbarExportRef}>
                  <ExportButton
                    onClick={() => setIsToolbarExportOpen((prev) => !prev)}
                    disabled={isExporting || data.length === 0}
                  >
                    <Download size={14} />
                    {isExporting ? 'Exportando...' : 'Exportar excel'}
                  </ExportButton>
                  {isToolbarExportOpen && (
                    <ToolbarExportDropdown>
                      <ToolbarExportItem
                        onClick={async () => {
                          setIsToolbarExportOpen(false)
                          await handleExport('all')
                        }}
                      >
                        Exportar tudo
                      </ToolbarExportItem>
                      <ToolbarExportItem
                        onClick={async () => {
                          setIsToolbarExportOpen(false)
                          await handleExport('table')
                        }}
                      >
                        Exportar tabela
                      </ToolbarExportItem>
                    </ToolbarExportDropdown>
                  )}
                </ToolbarExportWrapper>
              )}
            </ToolbarActions>
          </TableToolbar>
        )}
        <TableScroll>
          <GlobalTable>
            <Thead>
              {effectiveBulkHeaderRow}
              <HeaderLine>
                {selectable && (
                  <SortableTh style={{ width: '40px', minWidth: '40px', maxWidth: '40px', textAlign: 'center', padding: '6px' }}>
                    <ThContent style={{ justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#0090d3' }}
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        title="Selecionar todos os visíveis"
                      />
                    </ThContent>
                  </SortableTh>
                )}
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort()
                    const sortDirection = header.column.getIsSorted()
                    return (
                      <SortableTh
                        key={header.id}
                        $sortable={canSort}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <ThContent>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort &&
                            (sortDirection === 'asc' ? (
                              <ActiveSortIcon>
                                <ArrowUp size={14} />
                              </ActiveSortIcon>
                            ) : sortDirection === 'desc' ? (
                              <ActiveSortIcon>
                                <ArrowDown size={14} />
                              </ActiveSortIcon>
                            ) : (
                              <MutedSortIcon>
                                <ArrowUpDown size={14} />
                              </MutedSortIcon>
                            ))}
                        </ThContent>
                      </SortableTh>
                    )
                  }),
                )}
                {hasActionsColumn && (
                  <ActionsTh>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <span>{actionsHeader}</span>
                      {selectedCount > 0 && effectiveBulkActions.length > 0 && (
                        <RowActionsMenu actions={effectiveBulkActions} />
                      )}
                    </div>
                  </ActionsTh>
                )}
                {showColumnSettings && (
                  <GearTh>
                    <ColumnSettings
                      columns={columns}
                      columnsState={columnsState}
                      onChange={onColumnsChange!}
                      onRestore={onRestoreColumns!}
                      iconOnly
                    />
                  </GearTh>
                )}
              </HeaderLine>
            </Thead>
            <tbody>
              {rows.length === 0 ? (
                <FooterTr>
                  <Td colSpan={visibleColumnCount} style={{ padding: '4rem 0', textAlign: 'center' }}>
                    {isLoading ? (
                      <OrdenoLoading variant="inline" text="Carregando..." />
                    ) : (
                      <EmptyMessage>{emptyMessage}</EmptyMessage>
                    )}
                  </Td>
                </FooterTr>
              ) : (
                rows.map((row) => {
                  const rowId = getRowId ? getRowId(row.original) : ((row.original as any)?.id ?? row.id)
                  const isSelected = activeSelectedRowIds.has(rowId)
                  const canExpand = !!getSubRows && row.getCanExpand()
                  const handleRowClick = onRowClick
                    ? () => onRowClick(row.original)
                    : canExpand
                      ? () => row.toggleExpanded()
                      : undefined
                  return (
                    <Tr
                      key={row.id}
                      className={rowClassName ? rowClassName(row.original) : undefined}
                      title={rowTitle ? rowTitle(row.original) : undefined}
                      onClick={handleRowClick}
                      style={{
                        ...(row.depth > 0 ? { backgroundColor: '#fafbfc' } : {}),
                        ...(isSelected ? { backgroundColor: '#e0f2fe', borderLeft: '4px solid #0090d3' } : {}),
                      }}
                    >
                      {selectable && (
                        <Td
                          style={{ width: '40px', minWidth: '40px', maxWidth: '40px', textAlign: 'center', padding: '6px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#0090d3' }}
                            checked={isSelected}
                            onChange={(e) => handleRowSelect(rowId, e.target.checked)}
                          />
                        </Td>
                      )}
                      {row.getVisibleCells().map((cell) => (
                        <Td key={cell.id}>
                          {expandColumnId && cell.column.id === expandColumnId ? (
                            <ExpandCellContent $depth={row.depth}>
                              {canExpand && (
                                <ExpandToggle
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    row.toggleExpanded()
                                  }}
                                >
                                  {row.getIsExpanded() ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </ExpandToggle>
                              )}
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </ExpandCellContent>
                          ) : (
                            flexRender(cell.column.columnDef.cell, cell.getContext())
                          )}
                        </Td>
                      ))}
                      {hasActionsColumn && (
                        <ActionsTd className="edit-div">
                          {renderActions ? (
                            renderActions(row.original)
                          ) : (
                            <RowActionsMenu actions={getRowActions(row.original)} />
                          )}
                        </ActionsTd>
                      )}
                      {showColumnSettings && <GearTd />}
                    </Tr>
                  )
                })
              )}
            </tbody>
            {onLoadMore && (
              <tfoot>
                <FooterTr>
                  <Td colSpan={visibleColumnCount}>
                    <LoadMoreButton
                      currentCount={sortedData.length}
                      totalCount={totalCount}
                      onLoadMore={onLoadMore}
                      onLoadAll={onLoadAll}
                      hasMore={hasMore}
                      isLoading={isLoading}
                      label={countLabel}
                    />
                  </Td>
                </FooterTr>
              </tfoot>
            )}
          </GlobalTable>
        </TableScroll>
        {showLoadingOverlay && rows.length > 0 && (
          <OrdenoLoading variant="overlay" size="md" text="Carregando..." />
        )}
        {auditModalData && (
          <AuditHistoryModal
            tableName={auditModalData.tableName}
            recordId={auditModalData.recordId}
            recordTitle={auditModalData.recordTitle}
            onClose={() => setAuditModalData(null)}
          />
        )}
      </TableArea>
    </TableTooltipProvider>
  )
}

export default StandardDataTable
